import { useMutation } from '@tanstack/react-query';
import { Col, Row } from 'antd';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { EinvoiceHistoryApis } from 'apis/EinvoiceHistoryApis';
import { ButtonExport, ButtonFilter } from 'components';
import { useTitle } from 'contexts/TitleProvider';
import { useUser } from 'contexts/UserProvider';
import useQueryParam from 'hooks/useQueryPrams';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/constant';
import { exportExcelWithManySheet } from 'utils/constants/exportExcel';
import FilterEinvoiceHistory from './FilterEinvoiceHistory';
import TableEinvoiceHistory from './TableEinvoiceHistory';

const EinvoiceHistoryPage = () => {
  const { user } = useUser();
  const { setTitle, setBreadcrumb } = useTitle();

  const [data, setData] = useState<any>({ items: [], total: 0 });
  const [isOpenFilter, setIsOpenFilter] = useState(false);
  const [formParams, setFormParams] = useState<any>({});
  const [exportLoading, setExportLoading] = useState(false);

  const queryParam = useQueryParam();
  const page = parseInt(queryParam.get('page') + '') || 1;
  const page_size =
    parseInt(queryParam.get('page_size') + '') || DEFAULT_PAGE_SIZE;

  const defaultFilter = useMemo(() => {
    return {
      fromDateToDate: [dayjs().subtract(1, 'month'), dayjs()],
    };
  }, []);

  const mutateGetHistory = useMutation({
    mutationKey: ['getEinvoiceSendHistory'],
    mutationFn: (params: any) =>
      EinvoiceHistoryApis.getEinvoiceSendHistory(params),
    onSuccess: (res: any, variables: any) => {
      const result = res?.items
        ? res
        : res?.data?.items
          ? res.data
          : res?.data || { items: [], total: 0 };
      if (result) {
        result.currentPage =
          result.page || result.currentPage || variables?.page || 1;
        result.pageSize =
          result.limit ||
          result.pageSize ||
          variables?.limit ||
          DEFAULT_PAGE_SIZE;
      }
      if (variables?.type === 'export') {
        const summaryHeaderRow: any[] = [
          { key: 'STT', dataIndex: 'STT', title: 'STT' },
          { key: 'ChiNhanh', dataIndex: 'ChiNhanh', title: 'Chi Nhánh' },
          { key: 'MaHD', dataIndex: 'MaHD', title: 'Mã Bill' },
          { key: 'SoHD', dataIndex: 'SoHD', title: 'Số HĐ' },
          { key: 'KyHieu', dataIndex: 'KyHieu', title: 'Ký Hiệu' },
          {
            key: 'NgayPhatHanh',
            dataIndex: 'NgayPhatHanh',
            title: 'Ngày Phát Hành',
          },
          {
            key: 'GiaTriHD',
            dataIndex: 'GiaTriHD',
            title: 'Giá Trị HĐ',
            type: 'number',
            currency: 'vi-VN',
          },
          { key: 'MaKH', dataIndex: 'MaKH', title: 'Mã KH' },
          { key: 'TenKH', dataIndex: 'TenKH', title: 'Tên KH' },
          { key: 'LoaiKH', dataIndex: 'LoaiKH', title: 'Loại KH' },
          {
            key: 'SoLanGuiSMSTC',
            dataIndex: 'SoLanGuiSMSTC',
            title: 'Số Lần Gửi SMS Thành Công',
            type: 'number',
          },
          {
            key: 'SoLanGuiEmailTC',
            dataIndex: 'SoLanGuiEmailTC',
            title: 'Số Lần Gửi Email Thành Công',
            type: 'number',
          },
          {
            key: 'NgayGuiTC',
            dataIndex: 'NgayGuiTC',
            title: 'Ngày Gửi Thành Công',
          },
          { key: 'NguoiGuiTC', dataIndex: 'NguoiGuiTC', title: 'Người Gửi' },
          {
            key: 'SoLanGuiSMSTB',
            dataIndex: 'SoLanGuiSMSTB',
            title: 'Số Lần Gửi SMS Thất Bại',
            type: 'number',
          },
          {
            key: 'SoLanGuiEmailTB',
            dataIndex: 'SoLanGuiEmailTB',
            title: 'Số Lần Gửi Email Thất Bại',
            type: 'number',
          },
          {
            key: 'NgayGuiTB',
            dataIndex: 'NgayGuiTB',
            title: 'Ngày Gửi Thất Bại',
          },
          { key: 'NguoiGuiTB', dataIndex: 'NguoiGuiTB', title: 'Người Gửi' },
        ];

        const summaryData = (result.items || []).map(
          (item: any, idx: number) => {
            const formatUser = (val: string) => {
              let u = val || '-';
              if (u === 'AUTO_CREATE_EINVOICE') {
                u = '-';
              }
              return u === 'SAP_CALLBACK' ? 'SAP' : u;
            };

            return {
              STT: idx + 1,
              ChiNhanh: item.branchCode || '-',
              MaHD: item.billNum || '-',
              SoHD: item.invoiceNumber || '-',
              KyHieu: item.invoiceSerial || '-',
              NgayPhatHanh: item.publishDate
                ? dayjs(item.publishDate).format('DD/MM/YYYY')
                : '-',
              GiaTriHD: Number(item.invoiceAmount) || 0,
              MaKH: item.customerId || '-',
              TenKH: item.customerName || '-',
              LoaiKH: item.customerType || '-',
              SoLanGuiSMSTC: item.smsSuccessCount || 0,
              SoLanGuiEmailTC: item.emailSuccessCount || 0,
              NgayGuiTC: item.successTime
                ? dayjs(item.successTime).format('DD/MM/YYYY HH:mm:ss')
                : '-',
              NguoiGuiTC:
                item.successUser ||
                (item.smsSuccessCount > 0 || item.emailSuccessCount > 0
                  ? formatUser(item.userFullName)
                  : '-'),
              SoLanGuiSMSTB: item.smsFailedCount || 0,
              SoLanGuiEmailTB: item.emailFailedCount || 0,
              NgayGuiTB: item.failedTime
                ? dayjs(item.failedTime).format('DD/MM/YYYY HH:mm:ss')
                : '-',
              NguoiGuiTB:
                item.failedUser ||
                (item.emailFailedCount > 0 || item.smsFailedCount > 0
                  ? formatUser(item.userFullName)
                  : '-'),
            };
          },
        );

        const detailHeaderRow: any[] = [
          { key: 'STT', dataIndex: 'STT', title: 'STT' },
          { key: 'ChiNhanh', dataIndex: 'ChiNhanh', title: 'Chi Nhánh' },
          { key: 'MaHD', dataIndex: 'MaHD', title: 'Mã Bill' },
          { key: 'SoHD', dataIndex: 'SoHD', title: 'Số HĐ' },
          { key: 'KyHieu', dataIndex: 'KyHieu', title: 'Ký Hiệu' },
          {
            key: 'NgayPhatHanh',
            dataIndex: 'NgayPhatHanh',
            title: 'Ngày Phát Hành',
          },
          {
            key: 'GiaTriHD',
            dataIndex: 'GiaTriHD',
            title: 'Giá Trị HĐ',
            type: 'number',
            currency: 'vi-VN',
          },
          { key: 'MaKH', dataIndex: 'MaKH', title: 'Mã KH' },
          { key: 'TenKH', dataIndex: 'TenKH', title: 'Tên KH' },
          { key: 'LoaiKH', dataIndex: 'LoaiKH', title: 'Loại KH' },
          { key: 'MST', dataIndex: 'MST', title: 'MST' },
          { key: 'Email', dataIndex: 'Email', title: 'Email' },
          { key: 'SDT', dataIndex: 'SDT', title: 'SĐT' },
          { key: 'KenhGui', dataIndex: 'KenhGui', title: 'Kênh gửi' },
          {
            key: 'HinhThucGui',
            dataIndex: 'HinhThucGui',
            title: 'Hình thức gửi',
          },
          {
            key: 'ThoiGianGui',
            dataIndex: 'ThoiGianGui',
            title: 'Thời gian gửi',
          },
          { key: 'NguoiGui', dataIndex: 'NguoiGui', title: 'Người gửi' },
          {
            key: 'SoLanGui',
            dataIndex: 'SoLanGui',
            title: 'Số lần gửi',
            type: 'number',
          },
          {
            key: 'GuiLai',
            dataIndex: 'GuiLai',
            title: 'Gửi lại',
            type: 'number',
          },
          {
            key: 'LyDoGuiLai',
            dataIndex: 'LyDoGuiLai',
            title: 'Lý do gửi lại',
          },
          {
            key: 'TrangThaiGui',
            dataIndex: 'TrangThaiGui',
            title: 'Trạng thái gửi',
          },
          {
            key: 'ThoiGianThanhCong',
            dataIndex: 'ThoiGianThanhCong',
            title: 'Thời gian thành công',
          },
          {
            key: 'ThoiGianThatBai',
            dataIndex: 'ThoiGianThatBai',
            title: 'Thời gian thất bại',
          },
          { key: 'MaLoi', dataIndex: 'MaLoi', title: 'Mã lỗi' },
          { key: 'NoiDungLoi', dataIndex: 'NoiDungLoi', title: 'Nội dung lỗi' },
          {
            key: 'TransactionID',
            dataIndex: 'TransactionID',
            title: 'Transaction ID',
          },
          { key: 'LinkTraCuu', dataIndex: 'LinkTraCuu', title: 'Link tra cứu' },
          { key: 'NgayTao', dataIndex: 'NgayTao', title: 'Ngày tạo' },
          { key: 'NguoiTao', dataIndex: 'NguoiTao', title: 'Người tạo' },
          {
            key: 'NgayCapNhat',
            dataIndex: 'NgayCapNhat',
            title: 'Ngày cập nhật',
          },
          {
            key: 'NguoiCapNhat',
            dataIndex: 'NguoiCapNhat',
            title: 'Người cập nhật',
          },
        ];

        const detailData: any[] = [];
        let detailIdx = 0;
        (result.items || []).forEach((item: any) => {
          const itemLogs =
            item.logs && item.logs.length > 0 ? item.logs : [item];
          itemLogs.forEach((log: any) => {
            detailIdx++;
            const formatUser = (val: string) => {
              let u = val || item.userFullName || '-';
              if (u === 'AUTO_CREATE_EINVOICE') {
                u =
                  item.userFullName &&
                  item.userFullName !== 'AUTO_CREATE_EINVOICE'
                    ? item.userFullName
                    : '-';
              }
              return u === 'SAP_CALLBACK' ? 'SAP' : u;
            };

            detailData.push({
              STT: detailIdx,
              ChiNhanh: log.branchCode || item.branchCode || '-',
              MaHD: log.billNum || item.billNum || '-',
              SoHD: log.invoiceNumber || item.invoiceNumber || '-',
              KyHieu: log.invoiceSerial || item.invoiceSerial || '-',
              NgayPhatHanh:
                log.publishDate || item.publishDate
                  ? dayjs(log.publishDate || item.publishDate).format(
                      'DD/MM/YYYY',
                    )
                  : '-',
              GiaTriHD: Number(log.invoiceAmount ?? item.invoiceAmount ?? 0),
              MaKH: log.customerId || item.customerId || '-',
              TenKH: log.customerName || item.customerName || '-',
              LoaiKH: log.customerType || item.customerType || '-',
              MST: log.taxCode || item.taxCode || '-',
              Email: log.customerEmail || item.customerEmail || '-',
              SDT: log.customerPhone || item.customerPhone || '-',
              KenhGui: log.channel || '-',
              HinhThucGui: log.sendType === 'RESEND' ? 'Gửi lại' : 'Tự động',
              ThoiGianGui:
                log.sendTime || log.createdAt
                  ? dayjs(log.sendTime || log.createdAt).format(
                      'DD/MM/YYYY HH:mm:ss',
                    )
                  : '-',
              NguoiGui: formatUser(log.userFullName),
              SoLanGui: log.sendCount ?? 1,
              GuiLai: log.resendCount ?? 0,
              LyDoGuiLai: log.resendReason || '-',
              TrangThaiGui:
                log.sendStatus === 'SUCCESS'
                  ? 'Thành công'
                  : log.sendStatus === 'FAILED'
                    ? 'Thất bại'
                    : 'Đang xử lý',
              ThoiGianThanhCong: log.successTime
                ? dayjs(log.successTime).format('DD/MM/YYYY HH:mm:ss')
                : '-',
              ThoiGianThatBai: log.failedTime
                ? dayjs(log.failedTime).format('DD/MM/YYYY HH:mm:ss')
                : '-',
              MaLoi: log.errorCode || '-',
              NoiDungLoi: log.errorMessage || '-',
              TransactionID: log.transactionId || log.resendRequestId || '-',
              LinkTraCuu: log.lookupUrl || '-',
              NgayTao:
                log.createdAt || log.createDate
                  ? dayjs(log.createdAt || log.createDate).format(
                      'DD/MM/YYYY HH:mm:ss',
                    )
                  : '-',
              NguoiTao: formatUser(log.createdBy),
              NgayCapNhat:
                log.updatedAt || log.updateDate
                  ? dayjs(log.updatedAt || log.updateDate).format(
                      'DD/MM/YYYY HH:mm:ss',
                    )
                  : '-',
              NguoiCapNhat: formatUser(log.createdBy),
            });
          });
        });

        exportExcelWithManySheet(
          [
            {
              headerRow: summaryHeaderRow,
              data: summaryData,
              sheetName: 'Báo cáo tổng hợp',
              isShowSTT: false,
            },
            {
              headerRow: detailHeaderRow,
              data: detailData,
              sheetName: 'Báo cáo chi tiết',
              isShowSTT: false,
            },
          ],
          `Lich_Su_Gui_Hoa_Don_${dayjs().format('YYYYMMDD')}`,
        );
        setExportLoading(false);
      } else {
        setData(result);
      }
    },
  });

  useEffect(() => {
    setTitle('Lịch sử gửi hóa đơn');
    setBreadcrumb([
      { title: 'Kế toán/Thủ quỹ' },
      { title: 'Lịch sử gửi hóa đơn' },
    ]);
  }, [setTitle, setBreadcrumb]);

  const refetchData = useCallback(
    (isExport?: boolean) => {
      try {
        const filters = {
          ...defaultFilter,
          ...formParams,
        };

        const fromDateVal = filters.fromDateToDate?.[0]
          ? dayjs(filters.fromDateToDate[0]).startOf('day').format('YYYY-MM-DD')
          : undefined;
        const toDateVal = filters.fromDateToDate?.[1]
          ? dayjs(filters.fromDateToDate[1]).endOf('day').format('YYYY-MM-DD')
          : undefined;

        if (isExport) {
          setExportLoading(true);
        }

        mutateGetHistory.mutate({
          page: isExport ? 1 : page,
          limit: isExport ? data?.total || 1000 : page_size,
          branchCode: filters.plantId,
          billNum: filters.billNum,
          customerId: filters.customerId,
          fromDate: fromDateVal,
          toDate: toDateVal,
          soCode: filters.soCode,
          invoiceCode: filters.invoiceCode,
          invoiceSerial: filters.invoiceSerial,
          ...(isExport && {
            type: 'export',
          }),
        });
      } catch (error) {
        console.error('refetchData error:', error);
      }
    },
    [defaultFilter, formParams, page, page_size, mutateGetHistory, data?.total],
  );

  useEffect(() => {
    refetchData();
  }, [page, page_size, formParams]);

  const handleFinishFilter = (values: any) => {
    setFormParams(values);
  };

  const handleExportExcel = () => {
    refetchData(true);
  };

  return (
    <div>
      <Row justify="end" gutter={[8, 8]} className="!mb-4">
        <Col>
          <ButtonFilter isOpen={isOpenFilter} setIsOpen={setIsOpenFilter} />
        </Col>
        <Col>
          <ButtonExport
            title="Excel"
            loading={exportLoading}
            onClick={handleExportExcel}
            disabled={!data?.total}
          />
        </Col>
      </Row>

      <FilterEinvoiceHistory
        isOpen={isOpenFilter}
        defaultFilter={defaultFilter}
        onCancel={() => {
          setFormParams({});
        }}
        onFinish={handleFinishFilter}
        total={data?.total}
      />

      <TableEinvoiceHistory
        data={data}
        loading={mutateGetHistory.isPending}
        refetch={refetchData}
      />
    </div>
  );
};

export default EinvoiceHistoryPage;
